"use client";

import {
  FormBuilder,
  type FormBuilderHandle,
  type IFormBuilderSchema,
} from "@atlas/composite";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

interface IFormGeneratorProps {
  formData: { formConfig: string } | null | undefined;
  isEdit: boolean;
  onSchemaChange: (
    schema: IFormBuilderSchema | null,
    generatedJson?: string,
    isDirty?: boolean,
  ) => void;
  onSubmit?: (
    schema: IFormBuilderSchema,
    generatedJson: string,
  ) => void | Promise<void>;
}

export const FormGenerator = forwardRef<FormBuilderHandle, IFormGeneratorProps>(
  function CatalogTypeFormBuilderStep(
    { formData, isEdit, onSchemaChange, onSubmit },
    ref,
  ) {
    const [formSchema, setFormSchema] = useState<IFormBuilderSchema | null>(
      null,
    );
    const initialJsonRef = useRef<string | null>(null);
    const mountTimeRef = useRef<number>(Date.now());

    useEffect(() => {
      mountTimeRef.current = Date.now();
      if (formData?.formConfig) {
        try {
          const parsed = JSON.parse(formData.formConfig) as IFormBuilderSchema;
          setFormSchema(parsed);
          onSchemaChange(parsed, formData.formConfig, false);
        } catch {
          const empty = { fields: [], layout: [], layoutRows: [] };
          const emptyJson = JSON.stringify(empty, null, 2);
          setFormSchema(empty);
          onSchemaChange(empty, emptyJson, false);
        }
      } else if (!isEdit) {
        const empty = { fields: [], layout: [], layoutRows: [] };
        const emptyJson = JSON.stringify(empty, null, 2);
        setFormSchema(empty);
        onSchemaChange(empty, emptyJson, false);
      }
    }, [formData?.formConfig, isEdit, onSchemaChange]);

    const handleFormBuilderChange = useCallback(
      (schema: IFormBuilderSchema, generatedJson?: string) => {
        const json = generatedJson ?? JSON.stringify(schema, null, 2);

        // If this change happens within 1 second of mount/data load,
        // consider it part of the initialization phase and update the baseline.
        if (Date.now() - mountTimeRef.current < 1000) {
          initialJsonRef.current = json;
          onSchemaChange(schema, json, false);
        } else {
          const isDirty = json !== initialJsonRef.current;
          onSchemaChange(schema, json, isDirty);
        }
      },
      [onSchemaChange],
    );

    const handleFormBuilderSave = useCallback(
      (schema: IFormBuilderSchema, generatedJson?: string) => {
        const json = generatedJson ?? JSON.stringify(schema, null, 2);
        initialJsonRef.current = json;
        onSchemaChange(schema, json, false);
      },
      [onSchemaChange],
    );

    return (
      <div className="h-full min-h-0 overflow-hidden">
        <FormBuilder
          ref={ref}
          initialData={formSchema}
          onChange={handleFormBuilderChange}
          onSave={handleFormBuilderSave}
          onSubmit={onSubmit}
          showSaveButton={false}
          features={{
            formConfig: true,
            previewPopout: true,
          }}
        />
      </div>
    );
  },
);

FormGenerator.displayName = "FormGenerator";
